import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  LayoutDashboard, CalendarDays, Users,
  ClipboardList, TrendingUp, History, FileText, Bell, Star, UserPlus, Megaphone
} from 'lucide-react';

import { useAdminDashboard, useAdminHandlers, usePasswordChange, useAdminComputedData } from '../hooks/useAdminDashboard';
import { AdminHeader } from '../components/admin/AdminHeader';
import { DatePickerDialog } from '../components/admin/DatePickerDialog';
import { DashboardTab } from '../components/admin/DashboardTab';
import { BookingsTab } from '../components/admin/BookingsTab';
import { CalendarTab } from '../components/admin/CalendarTab';
import { TherapistsTab } from '../components/admin/TherapistsTab';
import { RevenueTab } from '../components/admin/RevenueTab';
import { CustomersTab } from '../components/admin/CustomersTab';
import { ManualsTab } from '../components/admin/ManualsTab';
import { RatesTab } from '../components/admin/RatesTab';
import { ReviewsTab } from '../components/admin/ReviewsTab';
import { FreelancersTab } from '../components/admin/FreelancersTab';
import { PromoBannerTab } from '../components/admin/PromoBannerTab';
import { PasswordChangeDialog } from '../components/admin/PasswordChangeDialog';
import { AddTherapistDialog, EditTherapistDialog, AssignTherapistDialog } from '../components/admin/TherapistDialogs';
import TherapistChat from '../components/TherapistChat';

const AdminDashboard = () => {
  const { t } = useLanguage();

  // Core data & navigation
  const dashboard = useAdminDashboard();
  const { user, token, logout, stats, bookings, therapists, activeTab, setActiveTab, newBookingCount, handleDismissNotification, authHeaders, fetchData, navigate } = dashboard;

  // Handlers for therapists, bookings, manuals, chat
  const handlers = useAdminHandlers(token, fetchData);

  // Password change
  const pwChange = usePasswordChange(token);

  // Computed data: revenue, calendar, customers, date pickers
  const computed = useAdminComputedData(bookings, therapists);

  // Pre-select preferred therapist when assign dialog opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (handlers.assignDialog.open && handlers.assignDialog.booking?.preferred_therapist) {
      const preferred = handlers.assignDialog.booking.preferred_therapist;
      const match = therapists.find(
        (th) => th.name.toLowerCase() === preferred.toLowerCase() && th.availability_status === 'available'
      );
      if (match) {
        handlers.setSelectedTherapist(match.id);
      }
    }
  }, [handlers.assignDialog.open, handlers.assignDialog.booking, therapists, handlers.setSelectedTherapist]);

  const getStatusBadge = (status) => {
    const configs = {
      pending: { label: t('common.pending'), variant: 'secondary', className: 'bg-yellow-100 text-yellow-800' },
      assigned: { label: t('common.assigned'), variant: 'secondary', className: 'bg-blue-100 text-blue-800' },
      accepted: { label: t('common.accepted'), variant: 'secondary', className: 'bg-purple-100 text-purple-800' },
      completed: { label: t('common.completed'), variant: 'secondary', className: 'bg-green-100 text-green-800' },
      cancelled: { label: t('common.cancelled'), variant: 'secondary', className: 'bg-red-100 text-red-800' },
    };
    const cfg = configs[status] || { label: status, variant: 'secondary', className: '' };
    return <Badge variant={cfg.variant} className={cfg.className}>{cfg.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader
        user={user}
        t={t}
        fetchData={fetchData}
        onPasswordChange={() => pwChange.setShowPasswordChange(true)}
        onLogout={() => { logout(); navigate('/'); }}
      />

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* New Booking Notification Banner */}
        {newBookingCount > 0 && (
          <div className="mb-4 bg-pink-50 border border-pink-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="new-booking-notification">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-pink-600 animate-bounce" />
              <span className="text-sm font-medium text-pink-800">
                {newBookingCount} new pending booking{newBookingCount > 1 ? 's' : ''} received!
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="text-pink-600 border-pink-300 hover:bg-pink-100"
                onClick={() => { setActiveTab('bookings'); handleDismissNotification(); }}>
                View
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismissNotification} className="text-pink-400 hover:text-pink-600">
                Dismiss
              </Button>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 sm:mb-6 w-full overflow-x-auto flex justify-start no-scrollbar">
            <TabsTrigger value="dashboard" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0">
              <LayoutDashboard className="w-4 h-4" /> <span className="hidden sm:inline">{t('admin.dashboard')}</span>
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0 relative">
              <ClipboardList className="w-4 h-4" /> <span className="hidden sm:inline">{t('admin.bookings')}</span>
              {newBookingCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{newBookingCount}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0">
              <CalendarDays className="w-4 h-4" /> <span className="hidden sm:inline">{t('admin.calendar')}</span>
            </TabsTrigger>
            <TabsTrigger value="therapists" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0">
              <Users className="w-4 h-4" /> <span className="hidden sm:inline">{t('admin.therapists')}</span>
            </TabsTrigger>
            <TabsTrigger value="revenue" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0">
              <TrendingUp className="w-4 h-4" /> <span className="hidden sm:inline">{t('admin.revenue')}</span>
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0">
              <History className="w-4 h-4" /> <span className="hidden sm:inline">{t('admin.customers')}</span>
            </TabsTrigger>
            <TabsTrigger value="manuals" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0" data-testid="manuals-tab">
              <FileText className="w-4 h-4" /> <span className="hidden sm:inline">{t('admin.manuals') || 'Manuals'}</span>
            </TabsTrigger>
            <TabsTrigger value="rates" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0" data-testid="rates-tab">
              <span className="text-sm font-bold">฿</span> <span className="hidden sm:inline">{t('admin.rates') || 'Rates'}</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0" data-testid="reviews-tab">
              <Star className="w-4 h-4" /> <span className="hidden sm:inline">Reviews</span>
            </TabsTrigger>
            <TabsTrigger value="freelancers" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0" data-testid="freelancers-tab">
              <UserPlus className="w-4 h-4" /> <span className="hidden sm:inline">Freelancers</span>
            </TabsTrigger>
            <TabsTrigger value="banner" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3 shrink-0" data-testid="banner-tab">
              <Megaphone className="w-4 h-4" /> <span className="hidden sm:inline">Banner</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab stats={stats} t={t} setActiveTab={setActiveTab} setShowAddTherapist={handlers.setShowAddTherapist} />
          </TabsContent>
          <TabsContent value="bookings">
            <BookingsTab bookings={bookings} t={t} getStatusBadge={getStatusBadge} formatDate={computed.formatDate}
              setAssignDialog={handlers.setAssignDialog} handleUpdateStatus={handlers.handleUpdateStatus} handleDeleteBooking={handlers.handleDeleteBooking}
              onOpenChat={(booking) => handlers.setChatBooking(booking)} />
          </TabsContent>
          <TabsContent value="calendar">
            <CalendarTab
              bookings={bookings} therapists={therapists}
              selectedDate={computed.selectedDate} setSelectedDate={computed.setSelectedDate}
              calendarMonth={computed.calendarMonth} setCalendarMonth={computed.setCalendarMonth}
              getDatesWithBookings={computed.getDatesWithBookings} selectedDateBookings={computed.selectedDateBookings}
              getStatusBadge={getStatusBadge} />
          </TabsContent>
          <TabsContent value="therapists">
            <TherapistsTab therapists={therapists} t={t} setShowAddTherapist={handlers.setShowAddTherapist}
              handleEditTherapist={handlers.handleEditTherapist} handleDeactivateTherapist={handlers.handleDeactivateTherapist}
              handleGenerateOTP={handlers.handleGenerateOTP} handleToggleAvailability={handlers.handleToggleAvailability} />
          </TabsContent>
          <TabsContent value="revenue">
            <RevenueTab
              revenuePeriod={computed.revenuePeriod} setRevenuePeriod={computed.setRevenuePeriod}
              revenueStartDate={computed.revenueStartDate} setRevenueStartDate={computed.setRevenueStartDate}
              revenueEndDate={computed.revenueEndDate} setRevenueEndDate={computed.setRevenueEndDate}
              revenueTherapistFilter={computed.revenueTherapistFilter} setRevenueTherapistFilter={computed.setRevenueTherapistFilter}
              currentRevenueData={computed.currentRevenueData} therapists={therapists} token={token}
              setShowDatePicker={computed.setShowDatePicker} setDatePickerMode={computed.setDatePickerMode} />
          </TabsContent>
          <TabsContent value="customers">
            <CustomersTab
              uniqueCustomers={computed.uniqueCustomers} filteredCustomers={computed.filteredCustomers}
              customerSearch={computed.customerSearch} setCustomerSearch={computed.setCustomerSearch}
              selectedCustomer={computed.selectedCustomer} setSelectedCustomer={computed.setSelectedCustomer}
              selectedCustomerBookings={computed.selectedCustomerBookings} selectedCustomerStats={computed.selectedCustomerStats}
              customerStartDate={computed.customerStartDate} setCustomerStartDate={computed.setCustomerStartDate}
              customerEndDate={computed.customerEndDate} setCustomerEndDate={computed.setCustomerEndDate}
              setShowCustomerDatePicker={computed.setShowCustomerDatePicker} setCustomerDatePickerMode={computed.setCustomerDatePickerMode}
              getStatusBadge={getStatusBadge} />
          </TabsContent>
          <TabsContent value="manuals">
            <ManualsTab t={t} therapists={therapists} sendingManual={handlers.sendingManual} handleSendManualViaLine={handlers.handleSendManualViaLine} token={token} />
          </TabsContent>
          <TabsContent value="rates">
            <RatesTab token={token} t={t} />
          </TabsContent>
          <TabsContent value="reviews">
            <ReviewsTab token={token} />
          </TabsContent>
          <TabsContent value="freelancers">
            <FreelancersTab token={token} />
          </TabsContent>
          <TabsContent value="banner">
            <PromoBannerTab token={token} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialogs */}
      <AddTherapistDialog
        open={handlers.showAddTherapist} onOpenChange={handlers.setShowAddTherapist}
        newTherapist={handlers.newTherapist} setNewTherapist={handlers.setNewTherapist}
        handlePhotoUpload={handlers.handlePhotoUpload} uploadingPhoto={handlers.uploadingPhoto}
        addingTherapist={handlers.addingTherapist} handleAddTherapist={handlers.handleAddTherapist} />

      <EditTherapistDialog
        open={handlers.showEditTherapist} onOpenChange={handlers.setShowEditTherapist}
        editData={handlers.editTherapistData} setEditData={handlers.setEditTherapistData}
        handlePhotoUpload={handlers.handlePhotoUpload} uploadingEditPhoto={handlers.uploadingEditPhoto}
        savingTherapist={handlers.savingTherapist} handleSaveTherapist={handlers.handleSaveTherapist} />

      <AssignTherapistDialog
        open={handlers.assignDialog.open}
        onOpenChange={(open) => handlers.setAssignDialog({ open, booking: open ? handlers.assignDialog.booking : null })}
        booking={handlers.assignDialog.booking} therapists={therapists}
        selectedTherapist={handlers.selectedTherapist} setSelectedTherapist={handlers.setSelectedTherapist}
        assigning={handlers.assigning} handleAssignTherapist={handlers.handleAssignTherapist}
        SelectComponents={{ Select, SelectContent, SelectItem, SelectTrigger, SelectValue }} />

      {/* Revenue Date Picker */}
      <DatePickerDialog
        open={computed.showDatePicker}
        onOpenChange={computed.setShowDatePicker}
        title={`${t('admin.selectDate')} (${computed.datePickerMode === 'start' ? t('admin.startDate') : t('admin.endDate')})`}
        mode={computed.datePickerMode}
        startDate={computed.revenueStartDate}
        endDate={computed.revenueEndDate}
        onSelect={(isoDate) => {
          if (computed.datePickerMode === 'start') computed.setRevenueStartDate(isoDate);
          else computed.setRevenueEndDate(isoDate);
        }}
      />

      {/* Customer Date Picker */}
      <DatePickerDialog
        open={computed.showCustomerDatePicker}
        onOpenChange={computed.setShowCustomerDatePicker}
        title={`${t('admin.selectDate')} (${computed.customerDatePickerMode === 'start' ? t('admin.startDate') : t('admin.endDate')})`}
        mode={computed.customerDatePickerMode}
        startDate={computed.customerStartDate}
        endDate={computed.customerEndDate}
        onSelect={(isoDate) => {
          if (computed.customerDatePickerMode === 'start') computed.setCustomerStartDate(isoDate);
          else computed.setCustomerEndDate(isoDate);
        }}
      />

      <PasswordChangeDialog
        open={pwChange.showPasswordChange} onOpenChange={pwChange.setShowPasswordChange}
        currentPassword={pwChange.currentPassword} setCurrentPassword={pwChange.setCurrentPassword}
        newPassword={pwChange.newPassword} setNewPassword={pwChange.setNewPassword}
        confirmPassword={pwChange.confirmPassword} setConfirmPassword={pwChange.setConfirmPassword}
        loading={pwChange.loading} error={pwChange.error} done={pwChange.done}
        onSubmit={pwChange.handleChangePassword} onReset={pwChange.resetPasswordChangeDialog} />

      <TherapistChat
        booking={handlers.chatBooking}
        open={!!handlers.chatBooking}
        onClose={() => handlers.setChatBooking(null)}
        authHeaders={authHeaders}
      />
    </div>
  );
};

export default AdminDashboard;
